import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  differenceInDays,
  differenceInHours,
  format,
  isBefore,
  parseISO,
  subHours,
} from 'date-fns';
import dayjs, { OpUnitType } from 'dayjs';
import { DAYS, WeekStringType } from '@src/constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export function convertCurrencyToShortForm(input?: string): string {
  if (!input) return '';
  const numeric = parseFloat(input.replace(/[^0-9.]/g, ''));

  if (isNaN(numeric)) return '';

  if (numeric >= 1_000_000) {
    return `${(numeric / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }

  if (numeric >= 1_000) {
    return `${(numeric / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }

  return numeric.toString();
}

export function formatCurrency(amount?: number, fractionDigits?: number) {
  if (!amount) return 0;

  return amount.toLocaleString('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits ?? 0,
  });
}

export function formatCentsAsDollars(cents?: number): string {
  if (!cents && cents !== 0) return '$0.00';

  const dollars = cents / 100;

  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatCentsAsDollarsShortForm(cents?: number): string {
  if (!cents && cents !== 0) return '$0';

  const dollars = cents / 100;

  if (dollars >= 1000000) {
    return `$${(dollars / 1000000).toFixed(2)}M`;
  }

  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(2)}K`;
  }

  return `$${dollars.toFixed(2)}`;
}

export function centsToUSD(cents?: number): number {
  if (!cents && cents !== 0) return 0;

  return cents / 100;
}

export function checkDate(isoDate?: string | null) {
  if (!isoDate) return false;

  try {
    const inputDate = new Date(isoDate);
    const currentDate = new Date();

    if (isNaN(inputDate.getTime())) return false;

    return inputDate > currentDate;
  } catch {
    return false;
  }
}

export function formatDate(date?: string, isMonthFirst?: boolean) {
  if (!date) return '';
  if (isMonthFirst) {
    return format(parseISO(date), 'MMM dd yyyy');
  }

  return format(parseISO(date), 'dd MMM yyyy');
}
export function formatDateToYears(date?: string) {
  if (!date) return '';

  return format(parseISO(date), 'yyyy');
}

export function formatDateTimeMST(date?: string) {
  if (!date) return '';
  const parsed = parseISO(date);
  const mstDate = subHours(parsed, 7); // Convert UTC to MST (UTC-7)

  return format(mstDate, "dd MMM yyyy, HH:mm 'MST'");
}

export function formatDate2(date?: string) {
  if (!date) return '';

  return format(parseISO(date), 'dd MMM yy');
}

export function formatDateByHours(date?: string, isAt?: boolean) {
  if (!date) return '';
  if (isAt) {
    // eslint-disable-next-line no-useless-escape
    return format(parseISO(date), `d MMM yyyy \'at\' HH:mm`);
  }

  return format(parseISO(date), 'dd MMM yy, HH:mm');
}

export const formatDateByMST = (dateString?: string) => {
  if (!dateString) return '';
  const date = parseISO(dateString);

  return format(date, "dd MMM yyyy HH:mm '(MST)'");
};

export function formatDateTimeShort(date?: string) {
  if (!date) return '';

  return format(parseISO(date), 'MM/dd/yy, hh:mm a');
}

interface ExpirationStatus {
  days: number;
  hours: number;
  displayText: string;
  cssClass: string;
  formattedDate: string;
  isHighlighted: boolean;
}

export function checkQualifyExpire(
  qualifyExpire?: Date | string | null
): ExpirationStatus | null {
  if (!qualifyExpire) return null;

  const today = new Date();
  const expireDate =
    qualifyExpire instanceof Date ? qualifyExpire : new Date(qualifyExpire);

  const days = differenceInDays(expireDate, today);
  const hours = differenceInHours(expireDate, today);

  const isHighlighted = days <= 2;
  const cssClass = isHighlighted ? 'text-[#C13839]' : '';

  const displayText = `${days} ${days === 1 ? 'day!' : 'days'}`;
  const formattedDate = format(expireDate, 'dd MMM yyyy');

  return {
    days,
    hours,
    displayText,
    cssClass,
    formattedDate,
    isHighlighted,
  };
}

/**
 * Convert a number to its ordinal string representation
 * @param num - The number to convert
 * @returns The ordinal string (e.g., 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th")
 */
export function toOrdinalString(num: number): string {
  if (isNaN(num)) return num.toString();

  const lastDigit = num % 10;
  const lastTwoDigits = num % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${num}th`;
  }

  switch (lastDigit) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
}

export function formatWithFullThresholds(value: number): string {
  const thresholds: number[] = [];

  for (let i = 1; i <= 9; i++) thresholds.push(i * 10);

  for (let i = 1; i <= 9; i++) thresholds.push(i * 100);

  for (let i = 1; i <= 9; i++) thresholds.push(i * 1000);

  for (let i = 1; i <= 9; i++) thresholds.push(i * 10000);

  for (let i = 1; i <= 9; i++) thresholds.push(i * 100000);

  thresholds.push(1000000);

  thresholds.sort((a, b) => a - b);

  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (value > thresholds[i]) {
      return `${thresholds[i].toLocaleString()}+`;
    }
  }

  return value <= 10 ? value.toString() : `10+`;
}

export const differenceDate = (dateString?: string) => {
  if (!dateString) return 0;
  const dueDate = parseISO(dateString);
  const today = new Date();

  if (isBefore(dueDate, today)) {
    return differenceInDays(today, dueDate);
  }

  return 0;
};

export function generateArrayNumber(start = 0, end = 0) {
  const array = [];

  for (let i = start; i <= end; i++) {
    array.push(i);
  }

  return array;
}

export const downloadFile = (blobPart: BlobPart, name: string) => {
  const excelBlob = new Blob([blobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const excelUrl = URL.createObjectURL(excelBlob);
  const link = document.createElement('a');

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `${name}-${timestamp}.xlsx`;

  link.href = excelUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(excelUrl);
  document.body.removeChild(link);
};

export type DateType = null | Date;

export type WeekDaysIndexType = (typeof DAYS)[number];

export type DateRangeType = {
  startDate: DateType;
  endDate: DateType;
};

export type DateValueType = DateRangeType | null;

export function dateIsValid(date: DateType) {
  return dayjs(date).isValid();
}

export function dateIsBefore(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isBefore(dayjs(b), unit);
}

export function dateIsAfter(a: Date, b: Date, unit: dayjs.OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  return dayjs(a).isAfter(dayjs(b), unit);
}

export function dateIsSameOrAfter(a: DateType, b: DateType, unit: OpUnitType) {
  if (!dateIsValid(a) || !dateIsValid(b)) return false;

  const dayA = dayjs(a);
  const dayB = dayjs(b);

  return dayA.isAfter(dayB, unit) || dayA.isSame(dayB, unit);
}

export function dateIsBetween(
  whoIsBetween: Date,
  start: Date,
  end: Date,
  unit: OpUnitType,
  include?: { start?: boolean; end?: boolean }
) {
  if (!dateIsValid(whoIsBetween) || !dateIsValid(start) || !dateIsValid(end)) {
    return false;
  }

  const target = dayjs(whoIsBetween);
  const startDay = dayjs(start);
  const endDay = dayjs(end);

  const isAfterStart = include?.start
    ? target.isSame(startDay, unit) || target.isAfter(startDay, unit)
    : target.isAfter(startDay, unit);

  const isBeforeEnd = include?.end
    ? target.isSame(endDay, unit) || target.isBefore(endDay, unit)
    : target.isBefore(endDay, unit);

  return isAfterStart && isBeforeEnd;
}

export function nextMonthBy(date: DateType) {
  if (!dateIsValid(date)) return dayjs().toDate();

  const parseDate = dayjs(date);

  return parseDate
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .month(parseDate.month() + 1)
    .toDate();
}

export function previousMonthBy(date: DateType) {
  if (!dateIsValid(date)) return dayjs().toDate();

  const parseDate = dayjs(date);

  return parseDate
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .month(parseDate.month() - 1)
    .toDate();
}

export function firstDayOfMonth(date?: Date) {
  return dayjs(date || dayjs())
    .startOf('month')
    .toDate();
}

export function dateUpdateMonth(date: DateType, value: number) {
  if (!dateIsValid(date)) return dayjs().toDate();

  return dayjs(date).month(value).toDate();
}

export function dateUpdateYear(date: DateType, value: number) {
  if (!dateIsValid(date)) return dayjs().toDate();

  return dayjs(date).year(value).toDate();
}

export function endDayOfMonth(date?: Date) {
  return dayjs(date || dayjs())
    .endOf('month')
    .toDate();
}

export function weekDayStringToIndex(
  dayString?: WeekStringType
): WeekDaysIndexType {
  switch (dayString) {
    case 'mon':
      return 0;
    case 'tue':
      return 1;
    case 'wed':
      return 2;
    case 'thu':
      return 3;
    case 'fri':
      return 4;
    case 'sat':
      return 5;
    case 'sun':
      return 6;
    default:
      return 0;
  }
}

export function dateAdd(date: Date, value: number, unit: dayjs.ManipulateType) {
  if (!dateIsValid(date)) return date;

  return dayjs(date).add(value, unit).toDate();
}

export function dayIndexInWeek(date?: Date) {
  return dayjs(date || dayjs()).day();
}

export function previousDaysInWeek(
  date: Date,
  weekStartDayIndex: WeekDaysIndexType = 0
) {
  if (!dateIsValid(date)) return [];

  const previousDays: Date[] = [];

  let i = 1;
  let previousDay = dateAdd(date, -i, 'day');

  while (dayIndexInWeek(previousDay) !== weekStartDayIndex) {
    previousDays.push(previousDay);
    i++;
    previousDay = dateAdd(date, -i, 'day');
  }

  return previousDays.sort((a, b) => {
    if (dateIsAfter(a, b, 'date')) return 1;

    return -1;
  });
}
export function daysInMonth(date?: dayjs.ConfigType) {
  const daysNumber = dayjs(date || dayjs()).daysInMonth();

  if (!daysNumber) return 0;

  return daysNumber;
}
export function allDaysInMonth(date?: Date) {
  if (!dateIsValid(date || new Date())) return [];

  const maxDaysInMonth = daysInMonth(date);

  const days: Date[] = [];

  for (let i = 1; i <= maxDaysInMonth; i++) {
    days.push(dayjs(date).date(i).toDate());
  }

  return days;
}

export function nextDaysInWeek(
  date: Date,
  weekStartDayIndex: WeekDaysIndexType = 0
) {
  if (!dateIsValid(date)) return [];

  const nextDays: Date[] = [];

  let i = 1;
  let nextDay = dateAdd(date, i, 'day');

  while (dayIndexInWeek(nextDay) !== weekStartDayIndex) {
    nextDays.push(nextDay);
    i++;
    nextDay = dateAdd(date, i, 'day');
  }

  return nextDays;
}

export function getNextDates(date: Date, limit: number) {
  if (!dateIsValid(date)) return [];

  const nexDates: Date[] = [];

  for (let i = 1; i <= limit; i++) {
    nexDates.push(dateAdd(date, i, 'day'));
  }

  return nexDates;
}

/**
 * Calculate period dates and formatted range string based on the report period
 * @param period - The report period enum value
 * @param customDateRange - Optional custom date range for 'exact' period
 * @param options - Options for return format
 * @returns Object with startDate, endDate (with time boundaries), and formatted range string
 */
export function getPeriodDates(
  period: string,
  customDateRange?: { startDate?: Date | null; endDate?: Date | null } | null
): { startDate: Date; endDate: Date; rangeString: string } | null {
  const now = dayjs();
  const formatDate = (date: dayjs.Dayjs) => date.format('MM/DD/YY');
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;
  let rangeString: string;

  switch (period) {
    case 'today':
      start = now.startOf('day');
      end = now.endOf('day');
      rangeString = formatDate(start);

      break;

    case 'yesterday':
      start = now.subtract(1, 'day').startOf('day');
      end = now.subtract(1, 'day').endOf('day');
      rangeString = formatDate(start);
      break;

    case 'this_week_sun_sat':
      start = now.subtract(now.day(), 'day').startOf('day');
      end = start.add(6, 'day').endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'this_week_mon_sun':
      start =
        now.day() === 0
          ? now.subtract(6, 'day').startOf('day')
          : now.subtract(now.day() - 1, 'day').startOf('day');
      end = start.add(6, 'day').endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_7_days':
      start = now.subtract(7, 'day').startOf('day');
      end = now.endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_week_sun_sat':
      start = now.subtract(now.day() + 7, 'day').startOf('day');
      end = start.add(6, 'day').endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_week_mon_sun':
      start =
        now.day() === 0
          ? now.subtract(13, 'day').startOf('day')
          : now.subtract(now.day() + 6, 'day').startOf('day');
      end = start.add(6, 'day').endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_business_week_mon_fri':
      start = now.subtract(((now.day() + 6) % 7) + 7, 'day').startOf('day');
      end = start.add(4, 'day').endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_14_days':
      start = now.subtract(14, 'day').startOf('day');
      end = now.endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'this_month':
      start = now.startOf('month');
      end = now.endOf('month');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_30_days':
      start = now.subtract(30, 'day').startOf('day');
      end = now.endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_month':
      start = now.subtract(1, 'month').startOf('month');
      end = now.subtract(1, 'month').endOf('month');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'this_quarter': {
      const quarterStartMonth = Math.floor(now.month() / 3) * 3;

      start = now.month(quarterStartMonth).startOf('month');
      end = now.month(quarterStartMonth + 2).endOf('month');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;
    }

    case 'last_quarter': {
      const lastQuarterStartMonth = Math.floor((now.month() - 3) / 3) * 3;

      start = now.month(lastQuarterStartMonth).startOf('month');
      end = now.month(lastQuarterStartMonth + 2).endOf('month');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;
    }

    case 'last_90_days':
      start = now.subtract(90, 'day').startOf('day');
      end = now.endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_6_months':
      start = now.subtract(6, 'month').startOf('day');
      end = now.endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_12_months':
      start = now.subtract(12, 'month').startOf('day');
      end = now.endOf('day');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'last_calendar_year':
      start = now.subtract(1, 'year').startOf('year');
      end = now.subtract(1, 'year').endOf('year');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'this_year':
      start = now.startOf('year');
      end = now.endOf('year');
      rangeString = `${formatDate(start)} - ${formatDate(end)}`;
      break;

    case 'all_time':
      start = dayjs('2000-01-01').startOf('day');
      end = now.endOf('day');
      rangeString = 'All Time';
      break;

    case 'exact':
      if (customDateRange?.startDate && customDateRange?.endDate) {
        start = dayjs(customDateRange.startDate).startOf('day');
        end = dayjs(customDateRange.endDate).endOf('day');
        rangeString = `${formatDate(start)} - ${formatDate(end)}`;
        break;
      }

      return null;

    default:
      return null;
  }

  return {
    startDate: start.toDate(),
    endDate: end.toDate(),
    rangeString,
  };
}

/**
 * Calculate the period range string based on the report period
 * @deprecated Use getPeriodDates instead
 * @param period - The report period enum value
 * @param customDateRange - Optional custom date range for 'exact' period
 * @returns Formatted date range string
 */
export function calculatePeriodRange(
  period: string,
  customDateRange?: { startDate?: Date | null; endDate?: Date | null } | null
): string {
  const result = getPeriodDates(period, customDateRange);

  return result?.rangeString ?? '';
}
